import { useToasts } from 'components/notifications/ToastProvider'

import withRenderClientsideOnly from 'components/utils/withRenderClientsideOnly'
import useDispatch from '../../hooks/useDispatch'
import useReduxSelector from '../../hooks/useReduxSelector'
import {
  useGetMyBucketListsQuery,
  useCreateBucketListMutation,
  BucketListFragmentFragmentDoc,
  useAddToBucketListMutation,
} from '../../hooks/useQueries'
import { closeCreateNewBucketList } from '../../actions/createNewBucketListActions'

import { buildMutationOptionsAddToBucketList } from '../../dashboard/MyBucketlists/gql/getBucketListItemsIndexHelpers'
import EditBucketListModal from './EditBucketlistModal'

const CreateNewBucketList = () => {
  const { addToast } = useToasts()
  const dispatch = useDispatch()

  const isOpen = useReduxSelector((state) => state.createNewBucketList.isOpen)
  const userId = useReduxSelector((state) => state.auth?.currentUser?.id)
  const itemToAddOnCreation = useReduxSelector(
    (state) => state.createNewBucketList.itemToAddOnCreation
  )
  const actionToReplayOnClose = useReduxSelector(
    (state) => state.createNewBucketList.actionToReplayOnClose
  )

  /*
    Fetch bucket lists if they are not already in the cache. If already in the cache, they will not be fetched.

    This is necessary because creating a new bucket list updates the cache by writing this query, as if it were being
    refetched. If it had not previously been fetched, the cache update will throw warnings.
  */
  useGetMyBucketListsQuery({
    fetchPolicy: 'cache-first',
  })

  const [createBucketList] = useCreateBucketListMutation()
  const [addToBucketList] = useAddToBucketListMutation()

  const handleClose = () => {
    dispatch(closeCreateNewBucketList())
    if (actionToReplayOnClose) {
      dispatch(actionToReplayOnClose)
    }
  }

  const handleCreateNewBucketList = (
    variables: GQL_CreateBucketListMutationVariables
  ) => {
    createBucketList({
      variables,
      onCompleted: async (data) => {
        const { id } = data.createBucketList.bucketList

        addToast('Bucket list created')

        if (itemToAddOnCreation) {
          const { itemId, itemType, source } = itemToAddOnCreation
          await addToBucketList({
            variables: {
              bucketListId: id,
              itemId,
              itemType,
              source,
            },
            ...buildMutationOptionsAddToBucketList({
              itemId,
              itemType,
              bucketListId: id,
            }),
            onCompleted: () => {
              addToast(`${itemType} added to bucket list`)
              handleClose()
            },
          })
        } else {
          handleClose()
        }
      },
      onError: (error) => {
        addToast(error.message, { appearance: 'error' })
      },
      update: async (cache, { data: mutationData }) => {
        const {
          id,
          name,
          theme,
          includesAccommodation,
        } = mutationData.createBucketList.bucketList

        // writeFragment returns a reference to the newly created bucket list
        const bucketListFragmenRef = cache.writeFragment({
          id: cache.identify({
            __typename: 'BucketList',
            id,
          }),
          fragment: BucketListFragmentFragmentDoc,
          fragmentName: 'BucketListFragment', // needed because there is another fragment nested underneath (bucket list theme)
          data: {
            id,
            name,
            theme,
            includesAccommodation,
          },
        })

        // update cache to include the new bucket list among those belonging to the current user
        cache.modify({
          id: cache.identify({
            __typename: 'CurrentUser',
            id: userId,
          }),
          fields: {
            bucketLists(cached = []) {
              return [bucketListFragmenRef, ...cached]
            },
          },
        })
      },
    })
  }

  return (
    <EditBucketListModal
      isOpen={isOpen}
      onSaveBucketlist={handleCreateNewBucketList}
      onRequestClose={handleClose}
      // Closing the modal also clears the state around the item to add to the newly created bucket list.
      // Therefore closing the modal should be delayed until after the item has been added, rather than on
      // submission of the new bucket list mutation (which is the default in <EditBucketListModal />).
      noCloseOnSubmit
    />
  )
}

export default withRenderClientsideOnly(CreateNewBucketList)
